import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useCustomerAnalytics } from "../../../services/hooks/useCustomerAnalytics";
import { useModalHook } from "../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import {
  removeOfferErrorReason,
  resetOutdatedSubItems,
} from "../../../services/redux/slices/cartAndLoyaltySlice";
import { selectUseLoyaltyService } from "../../../services/redux/selectors/loyaltyServiceSelectors";
import { removeLoyaltyOffer } from "../../../services/redux/slices/cartLoyaltyV2Slice";
import { shouldClearRedemptionOnAbandon } from "../../../services/redux/utils/loyaltyCartHelpers";
import { alert, logClientError } from "../../../utils/APICalls/APICalls";
import { COOKIE_ENUM, CUSTOMER_ANALYTICS_EVENT } from "../../../utils/enums";
import { AlertErrorType } from "../../../utils/ErrorTypes";
import { ItemOffer } from "../../../utils/OfferClass/ItemOffer/ItemOffer";
import { INVERTED_SECTIONS } from "../../../utils/types";

interface ChunkLoadError extends Error {
  name: "ChunkLoadError";
  type?: string;
  request?: string;
}

interface CSSChunkLoadError extends Error {
  code: "CSS_CHUNK_LOAD_FAILED";
  type: "error";
  request?: string;
}

type ChunkError = ChunkLoadError | CSSChunkLoadError;

interface DynamicImportResult {
  default?: React.ComponentType<any>;
}

// Type guard for chunk errors
const isChunkError = (err: unknown): err is ChunkError => {
  if (!err || typeof err !== "object") {
    return false;
  }

  const error = err as Record<string, any>;

  return (
    error.name === "ChunkLoadError" ||
    error.code === "CSS_CHUNK_LOAD_FAILED" ||
    (typeof error.message === "string" &&
      error.message.includes("Loading chunk")) ||
    (typeof error.message === "string" &&
      error.message.includes("Loading CSS chunk"))
  );
};

// Lightweight retry for both JS and CSS chunk load errors
const retryChunkLoad = async (
  importFn: () => Promise<DynamicImportResult>,
  maxRetries: number = 2
): Promise<DynamicImportResult> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await importFn();
    } catch (error: unknown) {
      if (isChunkError(error) && attempt < maxRetries) {
        const errorName =
          (error as ChunkLoadError).name || (error as CSSChunkLoadError).code;
        console.warn(`Chunk load attempt ${attempt} failed, retrying...`, {
          error: errorName,
          request: error.request,
        });

        // Short delay before retry
        await new Promise<void>((resolve) => setTimeout(resolve, 500));
        continue;
      }
      throw error;
    }
  }

  // This should never be reached due to the throw above, but TypeScript needs it
  throw new Error("Max retries exceeded");
};

export const Modal: React.FC = () => {
  const modal = useAppSelector((state) => state.modal.modal);
  const cartV2 = useAppSelector((state) => state.cartLoyaltyV2Reducer);
  const { handleModal } = useModalHook();
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const dispatch = useAppDispatch();
  const useLoyaltyService = useAppSelector(selectUseLoyaltyService);
  // ----------------------The component is loaded asynchronously while componentProps update synchronously from Redux, so the name it was loaded for is tracked to keep one modal from rendering another's props-----------------------
  const [loaded, setLoaded] = useState<{
    name: string;
    Component: React.ComponentType<any>;
  } | null>(null);
  const addCustomerAnalytics = useCustomerAnalytics();

  useEffect(() => {
    if (modal?.component) {
      const loadComponent = async (): Promise<void> => {
        try {
          const loadedModule = await retryChunkLoad(
            () =>
              import(
                /* webpackExclude: /\.test\.[jt]sx?$/ */
                `./Modals/${modal.component}`
              ) as Promise<DynamicImportResult>
          );

          if (loadedModule.default) {
            const Loaded = loadedModule.default;
            setLoaded({ name: String(modal.component), Component: Loaded });
          } else {
            alert(`${AlertErrorType.No_default_export_found_for_Modal}`, {
              modal: modal.component,
            });
            setLoaded(null);
          }
        } catch (e: unknown) {
          if (isChunkError(e)) {
            const errorType =
              (e as CSSChunkLoadError).code === "CSS_CHUNK_LOAD_FAILED"
                ? "CSS"
                : "JavaScript";
            logClientError(`${AlertErrorType.Error_loading_modal_component}`, {
              error: `Network error loading ${errorType} resources. Please refresh the page.`,
              modal: modal.component,
              chunkType: errorType,
              request: e.request,
            });
          } else {
            logClientError(`${AlertErrorType.Error_loading_modal_component}`, {
              error: e,
              modal: modal.component,
            });
          }
          setLoaded(null);
        }
      };

      loadComponent();
    } else {
      setLoaded(null);
    }
  }, [modal?.component]);

  const closeModal = (): void => {
    switch (modal?.component) {
      case "OffersPopup/OffersPopup":
      case "LoyaltyOffersPopup/LoyaltyOffersPopup":
      case "LoyaltyRedeemPopup/LoyaltyRedeemPopup": {
        const offerPopupDate: Date = new Date();
        offerPopupDate.setHours(offerPopupDate.getHours() + 3);
        const offerPopupExpires: Date = new Date(offerPopupDate);
        Cookies.set(COOKIE_ENUM.OFFER_MODAL, "true", {
          expires: offerPopupExpires,
        });
        break;
      }

      case "DownloadAppPopup/DownloadAppPopup":
        addCustomerAnalytics(
          CUSTOMER_ANALYTICS_EVENT.DOWNLOAD_APP_POPUP_DISMISS
        );
        break;

      case "OutdatedCartItems/OutdatedCartItems":
        dispatch(resetOutdatedSubItems());
        break;

      case "OfferError/OfferError":
        dispatch(removeOfferErrorReason());
        break;

      case "ItemDialog/ItemDialog": {
        ItemOffer.clearRedirectItem(cartV2);
        if (useLoyaltyService) {
          const loyaltyOfferId = (
            modal?.options?.componentProps as { loyaltyOfferId?: string }
          )?.loyaltyOfferId;
          if (
            shouldClearRedemptionOnAbandon(
              cartV2.loyalty?.redemption,
              loyaltyOfferId
            )
          ) {
            dispatch(removeLoyaltyOffer());
          }
        }
        break;
      }

      case "LoyaltyItemFromList/LoyaltyItemFromList": {
        const redemption = cartV2.loyalty?.redemption;
        const hasAddedOfferLines =
          (redemption?.addedLineIds?.length ?? 0) > 0 ||
          (redemption?.pendingSelections?.length ?? 0) > 0;
        const hasPendingItemCustomization =
          cartV2.loyalty?.applyFlow?.redirect?.itemId !== undefined;

        if (
          redemption?.offer &&
          !hasAddedOfferLines &&
          !hasPendingItemCustomization
        ) {
          dispatch(removeLoyaltyOffer());
        }
        break;
      }

      default:
        break;
    }
    handleModal(false, "", {});
    setLoaded(null);
  };

  // ----------------------A stale component would be handed the incoming modal's props, which crashes any modal with a required prop-----------------------
  const CustomComponent =
    loaded && loaded.name === modal?.component ? loaded.Component : null;

  if (!modal?.isOpen || !CustomComponent) {
    return null;
  }

  return (
    <div
      className={`custom_modal ${
        restaurantInfo?.colors?.invertedSections?.includes(
          INVERTED_SECTIONS.MODAL_WRAPPER
        )
          ? "invert"
          : ""
      }`}
      data-testid="modal"
    >
      <div
        className="modal_close"
        data-testid="modal-close-button"
        onClick={closeModal}
      ></div>
      <CustomComponent {...(modal?.options?.componentProps || {})} />
    </div>
  );
};
